import {AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, Optional, Output, ViewEncapsulation} from '@angular/core';
import {Translatable, TranslationEventService, TranslationService} from '@ngmedax/translation';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';

import {QuestionnaireEditorService} from '../../services/questionnaire-editor.service';
import {QuestionnaireStateService} from '../../services/questionnaire-state.service';
import {DomHelperService} from '../../services/dom-helper.service';
import {TEMPLATE_TO_ICON_MAPPING} from '../../../mappings';
import {TRANSLATION_EDITOR_SCOPE} from '../../../constants';
import {Question} from '../../../structure';
import {KEYS} from '../../../translation-keys';


// hack to inject decorator declarations. must occur before class declaration!
export interface AddQuestionComponent extends Translatable {}
declare const $: any;

@Component({
  selector: 'app-qa-add-question',
  templateUrl: './add-question.component.html',
  styleUrls: ['./add-question.component.css'],
  encapsulation: ViewEncapsulation.None
})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class AddQuestionComponent implements AfterViewInit {
  @Output() done: EventEmitter<any> = new EventEmitter();
  @Input() hideLabel = false;
  @Input() palette = false;
  @Input() position = null;
  @Input() cmpEl: ElementRef;
  @Input() question?: Questionnaire.Container;
  @Input() pageViewEnabled = false;
  @Input() pages: {startIndex: number; endIndex: number; title: string}[] = [];
  @Input() currentPageIndex = 0;

  /**
   * Default structures for question types
   * @type {Question.Structure}
   */
  public questionStructure = Question.Structure;

  /**
   * Select2 options
   * @type {any}
   */
  public options: any = {};

  /**
   * This is used to render the select2 options
   */
  public elementGroups: any[] = [];

  /**
   * Element format to icon mapping
   */
  public iconMapping = TEMPLATE_TO_ICON_MAPPING;

  /**
   * Injects dependencies
   */
  public constructor(
    private editor: QuestionnaireEditorService,
    private state: QuestionnaireStateService,
    private domHelper: DomHelperService,
    @Optional() private translationService: TranslationService,
    @Optional() private translationEvents: TranslationEventService
  ) {
    // adds matching icon on left side of element text
    const iconRenderer = (state) => {
      if (state.id && this.iconMapping[state.id]) {
        return $(`<span><i class="fa fa-fw ${this.iconMapping[state.id]}"></i> ${state.text.trim()}</span>`);
      }

      return state.text;
    };

    this.options = {
      width: '100%',
      placeholder: '...',
      templateResult: iconRenderer,
      templateSelection: iconRenderer
    };

    this.updateElements();
    this.translationEvents && this.translationEvents.onLocaleChanged().subscribe(() => this.updateElements());
  }

  /**
   * Adds element to copy question
   */
  public ngAfterViewInit() {
    if (this.question) {
      this.elementGroups.unshift({
        title: this._(KEYS.EDITOR.DYNAMIC),
        elements: [{
          title: this._(KEYS.EDITOR.COPY_QUESTION),
          value: 'COPY'
        }]
      });

      this.questionStructure['COPY'] = JSON.parse(JSON.stringify(this.question));
    }
  }

  /**
   * Sadly, NgbPopover does not support auto hide on click outside, so we have to implement this.
   * When Ngb is updated to a version >= 3.0.0, we can use the "autoClose" option.
   *
   * @param targetElement
   */
  @HostListener('document:click', ['$event.target'])
  public onClick(targetElement) {
    const popover = $('ngb-popover-window');
    const select2Dropdown = $('.select2-dropdown');
    const clickInside = popover.find(targetElement).length || select2Dropdown.find(targetElement).length;
    (!clickInside) && $('ngb-popover-window').hide();
  }

  /**
   * Event for when we should add a questionnaire element.
   *
   * @param {Questionnaire.Container} question
   */
  public onAddElement(templateName: string, targetPosition: number = null) {
    this.done.emit();

    const template = templateName ? this.questionStructure[templateName] : '';

    if (!template) {
      return;
    }

    let question: Questionnaire.Container;

    // popover beside a question sets `this.question` so we can offer COPY; every other choice must build from the template or the current question was wrongly duplicated (MYM-969).
    if (templateName === 'COPY' && this.question) {
      // copy of an existing question - duplicate keeps child ids, regenerates
      // the top-level uuid and remaps mapped variables and conditions.
      question = this.state.duplicateElement(this.question);
    } else {
      // brand new question from a template
      question = JSON.parse(JSON.stringify(template));
      question.id = this.editor.generateUUID();

      if (question.elements) {
        for (const element of question.elements) {
          if (element.id) {
            continue;
          }

          element.id = this.editor.generateUUID();
        }
      }
    }

    // calculate insert position
    let insertPosition = targetPosition === null ? this.position : targetPosition;

    // in page view, insert at end of current page (before pageBreak if exists)
    if (this.pageViewEnabled && this.pages.length > 0 && insertPosition === null) {
      const currentPage = this.pages[this.currentPageIndex];
      if (currentPage) {
        const questions = this.state.getQuestionnaire().questions;
        const lastElement = questions[currentPage.endIndex];
        // insert before pageBreak, or at end of page
        if (lastElement?.format === 'pageBreak') {
          insertPosition = currentPage.endIndex - 1;
        } else {
          insertPosition = currentPage.endIndex;
        }
      }
    }

    // add element to questionnaire
    this.state.addElement(question, insertPosition);

    // animate component to signalize that an element was added to the questionnaire
    (this.cmpEl) && this.domHelper.animate(this.cmpEl.nativeElement, ['fadeIn', 'animated']);
  }

  /**
   * Updates available elements
   */
  public updateElements() {
    this.elementGroups = [{
      title: this._(KEYS.EDITOR.STATIC),
      elements: [{
        title: this._(KEYS.EDITOR.INFO_TEXT),
        value: 'INFO'
      }, {
        title: this._(KEYS.EDITOR.EMAIL),
        value: 'EMAIL'
      }, {
        title: this._(KEYS.EDITOR.PAGE_BREAK),
        value: 'PAGE_BREAK'
      }]
    }, {
      title: this._(KEYS.EDITOR.INPUT),
      elements: [{
        title: this._(KEYS.EDITOR.NUMBER),
        value: 'NUMERIC'
      }, {
        title: this._(KEYS.EDITOR.DATE),
        value: 'DATE'
      }, {
        title: this._(KEYS.EDITOR.FREE_TEXT),
        value: 'TEXT'
      }, {
        title: this._(KEYS.EDITOR.TEXT_MULTILINE),
        value: 'TEXT_MULTILINE'
      }, {
        title: this._(KEYS.EDITOR.MULTI_VALUE),
        value: 'MULTI_VALUE'
      }]
    }, {
      title: this._(KEYS.EDITOR.CHOICE),
      elements: [{
        title: this._(KEYS.EDITOR.SINGLE_CHOICE),
        value: 'SINGLE_CHOICE'
      }, {
        title: this._(KEYS.EDITOR.MULTIPLE_CHOICE),
        value: 'MULTIPLE_CHOICE'
      }, {
        title: this._(KEYS.EDITOR.QUESTION_MATRIX),
        value: 'MATRIX'
      }, {
        title: this._(KEYS.EDITOR.SCALE),
        value: 'SCALE'
      }]
    }, {
      title: this._(KEYS.EDITOR.MEDIA),
      elements: [{
        title: this._(KEYS.EDITOR.TAKE_PICTURE),
        value: 'PICTURE'
      }, {
        title: this._(KEYS.EDITOR.DRAWING),
        value: 'CANVAS'
      }, {
        title: this._(KEYS.EDITOR.DOCUMENT_SCAN),
        value: 'DOCUMENT_SCAN'
      }, {
        title: this._(KEYS.EDITOR.SIGNATURE),
        value: 'SIGNATURE'
      }]
    }, {
      title: this._(KEYS.EDITOR.DIAGRAMS),
      elements: [{
        title: this._(KEYS.EDITOR.NET_DIAGRAM),
        value: 'NET_DIAGRAM'
      }, {
        title: this._(KEYS.EDITOR.BAR_CHART),
        value: 'BAR_CHART'
      }, {
        title: this._(KEYS.EDITOR.COLUMN_CHART),
        value: 'COLUMN_CHART'
      }, {
        title: this._(KEYS.EDITOR.LINE_CHART),
        value: 'LINE_CHART'
      }, {
        title: this._(KEYS.EDITOR.PIE_CHART),
        value: 'PIE_CHART'
      }]
    }, {
      title: this._(KEYS.EDITOR.TEMPLATES),
      elements: [{
        title: this._(KEYS.EDITOR.YES_NO),
        value: 'YES_NO'
      }, {
        title: this._(KEYS.EDITOR.YES_NO_MISC),
        value: 'YES_NO_MISC'
      }, {
        title: this._(KEYS.EDITOR.MULTIPLE_CHOICE_MISC),
        value: 'MULTIPLE_CHOICE_MISC'
      }]
    }];
  }

}
